import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../../theme/useTheme';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../../store';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
  TextInput,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../constants/fonts';
import { AdminColors } from '../../constants/adminColors';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import LHDataTable from '../../../../components/LHDataTable';
import { t } from './i18n';
import { EquipmentLanguage } from './types';
import { LoadSequenceItem } from './types';
import { ListLanguageToggle } from './EquipmentConfig';
import {
  fetchLoadSequences,
  createLoadSequence,
  updateLoadSequence,
  checkLoadSequenceExists,
  checkLoadSequenceDisplayOrderExists,
  toggleLoadSequenceActive,
} from './perfectTruckApi';
import { useToast } from '../../../../components/LhToastContainer';

interface ErrorMap { [key: string]: string }

const LoadSequenceConfig: React.FC<{ listLanguage?: EquipmentLanguage }> = ({ listLanguage }) => {
  const currentListLanguage = listLanguage ?? 'en';
  const [modalLanguage, setModalLanguage] = useState<EquipmentLanguage>('en');
  const [enForm, setEnForm] = useState({ loadSequence: '' });
  const [frForm, setFrForm] = useState({ loadSequence: '' });
  const [esForm, setEsForm] = useState({ loadSequence: '' });
  const [modalValidationError, setModalValidationError] = useState<string>('');
  const [modalLoading, setModalLoading] = useState(false);

  const [items, setItems] = useState<LoadSequenceItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [apiError, setApiError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [editItem, setEditItem] = useState<LoadSequenceItem | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const [formLoadSequence, setFormLoadSequence] = useState('');

  const [errors, setErrors] = useState<ErrorMap>({});
  const [globalError, setGlobalError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const toast = useToast();
  const requestedBy = useSelector((state: RootState) =>
    state.appData?.userInfo?.email ?? state.auth?.user?.email ?? state.auth?.user?.username ?? ''
  );

  const loadItems = useCallback(async () => {
    try {
      console.debug('[LoadSequenceConfig] loadItems called, listLanguage=', currentListLanguage);
      setIsLoading(true);
      setApiError('');
      // Call API with current list language so backend returns translations appropriately
      const data = await fetchLoadSequences(currentListLanguage);
      setItems(data);
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to load load sequences.');
    } finally {
      setIsLoading(false);
    }
  }, [listLanguage]);

  useEffect(() => { loadItems(); }, [listLanguage]);

  const validate = (loadSequence: string): ErrorMap => {
    const errs: ErrorMap = {};
    if (!loadSequence.trim()) {
      errs.loadSequence = 'Load sequence value is required.';
    } else if (loadSequence.length > 10) {
      errs.loadSequence = 'Load sequence value cannot exceed 10 characters.';
    } else if (!/^[A-Za-z0-9]+$/.test(loadSequence.trim())) {
      errs.loadSequence = 'Load sequence must be alphanumeric.';
    } else if (!editItem) {
      const dup = items.find(
        i => i.loadSequence.toLowerCase() === loadSequence.trim().toLowerCase(),
      );
      if (dup) errs.loadSequence = `"${loadSequence.trim()}" already exists.`;
    }
    return errs;
  };

  const handleToggleActive = async (id: number) => {
    const item = items.find(i => i.id === id);
    if (!item) return;
    if (item.isActive) {
      const activeCount = items.filter(i => i.isActive).length;
      if (activeCount <= 1) {
        setGlobalError('At least one load sequence must remain active.');
        return;
      }
    }
    setGlobalError('');
    try {
      const updated = await toggleLoadSequenceActive(id, !item.isActive, requestedBy);
      setItems(prev => prev.map(i => i.id === id ? updated : i));
      toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess.loadSequence'), variant: 'success' });
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to toggle active status.');
    }
  };

  const openAdd = () => {
    setIsAdding(true);
    setEditItem(null);
    setFormLoadSequence('');
    setEnForm({ loadSequence: '' }); setFrForm({ loadSequence: '' }); setEsForm({ loadSequence: '' });
    setModalLanguage('en'); setModalValidationError('');
    setErrors({});
    setGlobalError('');
    setApiError('');
    setHasUnsavedChanges(false);
  };

  const openEdit = (item: LoadSequenceItem) => {
    setIsAdding(false);
    setEditItem(item);
    setFormLoadSequence(item.loadSequence);
    setEnForm({ loadSequence: item.loadSequence }); setFrForm({ loadSequence: '' }); setEsForm({ loadSequence: '' });
    setModalLanguage('en'); setModalValidationError('');
    setErrors({});
    setGlobalError('');
    setApiError('');
    setHasUnsavedChanges(false);
    // Load translations for this load sequence across languages
    (async () => {
      try {
        setModalLoading(true);
        const [enRes, frRes, esRes] = await Promise.all([
          fetchLoadSequences('en').catch(() => []),
          fetchLoadSequences('fr').catch(() => []),
          fetchLoadSequences('es').catch(() => []),
        ]);
        const findById = (arr: LoadSequenceItem[]) => arr.find(r => r.id === item.id);
        const en = findById(enRes as LoadSequenceItem[]);
        const fr = findById(frRes as LoadSequenceItem[]);
        const es = findById(esRes as LoadSequenceItem[]);
        setEnForm({ loadSequence: en?.loadSequence ?? item.loadSequence });
        setFrForm({ loadSequence: fr?.loadSequence ?? '' });
        setEsForm({ loadSequence: es?.loadSequence ?? '' });
      } catch (e) {
        // ignore
      } finally {
        setModalLoading(false);
      }
    })();
  };

  const doClose = () => {
    if (hasUnsavedChanges) {
      setShowConfirmClose(true);
    } else {
      setIsAdding(false);
      setEditItem(null);
      setFormLoadSequence('');
      setErrors({});
      setHasUnsavedChanges(false);
    }
  };

  const confirmClose = () => {
    setIsAdding(false);
    setEditItem(null);
    setFormLoadSequence('');
    setErrors({});
    setHasUnsavedChanges(false);
    setShowConfirmClose(false);
  };

  const handleSave = async () => {
    const englishValue = (enForm.loadSequence ?? '').trim();
    const errs = validate(englishValue);
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    // Validate translations: require all languages completed
    const translations = { en: enForm, fr: frForm, es: esForm } as any;
    const missing: string[] = [];
    (['en','fr','es'] as EquipmentLanguage[]).forEach(lang => {
      const tform = translations[lang];
      if (!tform || !tform.loadSequence || !tform.loadSequence.trim()) missing.push(lang);
    });
    if (missing.length > 0) {
      setModalValidationError(t(modalLanguage, 'validation.completeTranslations'));
      return;
    }
    try {
      setIsSaving(true);
      if (editItem) {
        // Build multilingual payloads
        const common = {
          displayOrder: editItem.displayOrder,
          locationType: editItem.locationType,
          organizationType: editItem.organizationType,
          is3pl: editItem.is3pl,
          isActive: editItem.isActive,
          requestedBy,
        } as any;
        const make = (lang: EquipmentLanguage, f: any) => ({
          loadSequence: (f.loadSequence ?? '').trim(),
          displayOrder: common.displayOrder,
          locationType: common.locationType,
          organizationType: common.organizationType,
          is3pl: common.is3pl,
          isActive: common.isActive,
          requestedBy: common.requestedBy,
          language: lang,
        });
        const payloadArray = [make('en', enForm), make('fr', frForm), make('es', esForm)];
        const updated = await updateLoadSequence(editItem.id, payloadArray as any);
        setItems(prev => prev.map(i => i.id === editItem.id ? updated : i));
        toast.showToast({ header: t(currentListLanguage, 'toast.updateSuccess.loadSequence'), variant: 'success' });
      } else {
        const exists = await checkLoadSequenceExists(englishValue);
        if (exists) {
          setErrors({ loadSequence: 'Load Sequence Already Exists' });
          setIsSaving(false);
          return;
        }
        const nextDisplayOrder = items.length > 0
          ? Math.max(...items.map(i => i.displayOrder)) + 1
          : 1;
        const common = { displayOrder: nextDisplayOrder, locationType: null, organizationType: null, is3pl: false, isActive: true, requestedBy } as any;
        const make = (lang: EquipmentLanguage, f: any) => ({
          loadSequence: (f.loadSequence ?? '').trim(),
          displayOrder: common.displayOrder,
          locationType: common.locationType,
          organizationType: common.organizationType,
          is3pl: common.is3pl,
          isActive: common.isActive,
          requestedBy: common.requestedBy,
          language: lang,
        });
        const payloadArray = [make('en', enForm), make('fr', frForm), make('es', esForm)];
        const created = await createLoadSequence(payloadArray as any);
        setItems(prev => [...prev, created]);
        toast.showToast({ header: t(currentListLanguage, 'toast.addSuccess.loadSequence'), variant: 'success' });
      }
      confirmClose();
    } catch (e: any) {
      setApiError(e?.response?.data?.message ?? 'Failed to save load sequence.');
    } finally {
      setIsSaving(false);
    }
  };

  const sorted = [...items].sort((a, b) => a.displayOrder - b.displayOrder);
  const filtered = searchQuery.trim()
    ? sorted.filter(i => i.loadSequence.toLowerCase().includes(searchQuery.trim().toLowerCase()))
    : sorted;

  return (
    <View>
      {/* Info banner */}
      <View style={styles.infoBanner}>
        <MaterialIcons name="check" size={16} color={AdminColors.blue700} style={styles.infoIcon} />
        <Text style={styles.infoText}>{t(currentListLanguage, 'info.loadSequenceBanner')}</Text>
      </View>

      {/* Toolbar: search + add */}
      <View style={styles.toolbar}>
        <View style={styles.searchBox}>
          <MaterialIcons name="search" size={16} color={AdminColors.textMuted} style={{ marginRight: 6 }} />
          <TextInput
            style={styles.searchInput}
            placeholder={t(currentListLanguage, 'searchPlaceholder')}
            placeholderTextColor={AdminColors.textMuted}
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')} activeOpacity={0.7}>
              <MaterialIcons name="close" size={14} color={AdminColors.textMuted} />
            </TouchableOpacity>
          )}
        </View>
        {!isAdding && !editItem && (
          <AdminButton
            label={t(currentListLanguage, 'addButton.loadSequence')}
            variant="primary"
            small
            onPress={openAdd}
          />
        )}
      </View>

      {(globalError || apiError) ? (
        <Text style={styles.globalError}>{globalError || apiError}</Text>
      ) : null}

      {(isAdding || editItem) && (
        <View style={styles.inlineForm}>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <Text style={styles.fieldLabel}>{t(modalLanguage, 'form.loadSequenceValue')} <Text style={{ color: AdminColors.red400 }}>*</Text></Text>
            <View style={{ marginLeft: 12 }}>
              <ListLanguageToggle value={modalLanguage} onChange={(l) => { setModalLanguage(l); setModalValidationError(''); }} compact />
            </View>
          </View>

          <AdminFormField label="" error={errors.loadSequence || modalValidationError}>
            <AdminTextInput
              value={(modalLanguage === 'en' ? enForm.loadSequence : modalLanguage === 'fr' ? frForm.loadSequence : esForm.loadSequence)}
              onChangeText={v => {
                if (modalLanguage === 'en') setEnForm(prev => ({ ...prev, loadSequence: v }));
                else if (modalLanguage === 'fr') setFrForm(prev => ({ ...prev, loadSequence: v }));
                else setEsForm(prev => ({ ...prev, loadSequence: v }));
                setErrors({}); setHasUnsavedChanges(true); setModalValidationError('');
              }}
              placeholder={t(modalLanguage, 'placeholder.example')}
              hasError={!!errors.loadSequence || !!modalValidationError}
            />
          </AdminFormField>
          <View style={styles.inlineFormActions}>
            <AdminButton label={t(currentListLanguage, 'button.cancel') ?? 'Cancel'} variant="secondary" onPress={doClose} />
            <AdminButton
              label={editItem ? (t(currentListLanguage, 'button.saveChanges') ?? 'Update') : (t(currentListLanguage, 'button.save') ?? 'Save')}
              variant="primary"
              onPress={handleSave}
              disabled={isSaving}
            />
          </View>
        </View>
      )}

      {/* Unsaved Changes Confirmation Modal */}
      <AdminModal
        visible={showConfirmClose}
        title={t(currentListLanguage, 'modal.unsavedTitle')}
        onClose={() => setShowConfirmClose(false)}
        footer={
          <>
            <AdminButton label={t(currentListLanguage, 'modal.unsaved.continue')} variant="secondary" onPress={() => setShowConfirmClose(false)} />
            <AdminButton label={t(currentListLanguage, 'modal.unsaved.close')} variant="primary" onPress={confirmClose} />
          </>
        }
      >
        <Text style={styles.confirmText}>{t(currentListLanguage, 'modal.unsaved.message')}</Text>
      </AdminModal>

      {isLoading ? (
        <View style={styles.loadingWrapper}>
          <ActivityIndicator size="large" color={AdminColors.blue700} />
        </View>
      ) : (
        <View style={styles.surface}>
          <LHDataTable
            data={filtered}
            columns={[
              { columnKey: 'loadSequence', header: t(currentListLanguage, 'table.loadSequence'), flexGrow: 1, sortable: true },
              { columnKey: 'displayOrder', header: t(currentListLanguage, 'table.displayOrder'), width: 140, sortable: true },
              {
                columnKey: 'isActive',
                header: t(currentListLanguage, 'table.active'),
                width: 120,
                sortable: false,
                renderCell: (item: any) => (
                  <TouchableOpacity onPress={() => handleToggleActive(item.id)} activeOpacity={0.7}>
                    <View style={[styles.statusBadge, item.isActive ? styles.statusActive : styles.statusInactive]}>
                      <Text style={[styles.statusBadgeText, item.isActive ? styles.statusActiveText : styles.statusInactiveText]}>
                        {item.isActive ? t(currentListLanguage, 'counts.active') : t(currentListLanguage, 'counts.inactive')}
                      </Text>
                    </View>
                  </TouchableOpacity>
                ),
              },
            ]}
            tableHeader=""
            tableDescription={`Showing ${filtered.length} of ${items.length} Load Sequences (${items.filter(i => i.isActive).length} ${t(currentListLanguage, 'counts.active')}, min 1 required)`}
            showHeaderTopBorder={false}
            onRowClick={(item) => openEdit(item as LoadSequenceItem)}
            disableRowHighlight
            locale={{
              rowsPerPageLabel: t(currentListLanguage, 'pagination.rowsPerPage'),
              pageLabel: t(currentListLanguage, 'pagination.page'),
              pageOfLabel: t(currentListLanguage, 'pagination.of'),
              firstPageLabel: t(currentListLanguage, 'pagination.first'),
              prevPageLabel: t(currentListLanguage, 'pagination.previous'),
              nextPageLabel: t(currentListLanguage, 'pagination.next'),
              lastPageLabel: t(currentListLanguage, 'pagination.last'),
            }}
          />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  infoBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: AdminColors.blue50,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: 12,
    marginBottom: 4,
  },
  infoIcon: {
    marginRight: 8,
    marginTop: 1,
  },
  infoText: {
    flex: 1,
    fontFamily: FontFamily.regular,
    fontSize: 13,
    color: AdminColors.textPrimary,
    lineHeight: 20,
  },
  toolbar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 16,
    marginBottom: 4,
    gap: 12,
    flexWrap: 'wrap',
  },
  searchBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 34,
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 6,
    backgroundColor: AdminColors.bgSurface,
    minWidth: 200,
    flex: 1,
    maxWidth: 300,
  },
  searchInput: {
    flex: 1,
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
    outlineWidth: 0,
  } as any,
  loadingWrapper: {
    paddingVertical: 48,
    alignItems: 'center',
  },
  globalError: {
    fontFamily: FontFamily.bold,
    fontSize: 13,
    color: AdminColors.red400,
    backgroundColor: AdminColors.red75,
    padding: 12,
    marginBottom: 8,
    borderRadius: 8,
  },
  surface: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    ...Platform.select({
      web: { boxShadow: '0 0 24px rgba(0,94,192,0.08)' } as any,
      default: {
        shadowColor: '#005EC0',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0.08,
        shadowRadius: 24,
        elevation: 2,
      },
    }),
    overflow: 'hidden',
    marginTop: 12,
  },
  inlineForm: {
    backgroundColor: AdminColors.bgSurface,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: AdminColors.blue200,
    padding: 16,
    marginBottom: 12,
  },
  inlineFormActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 12,
  },
  statusBadge: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    borderRadius: 12,
    alignSelf: 'center',
  },
  statusActive: {
    backgroundColor: AdminColors.green75,
  },
  statusInactive: {
    backgroundColor: AdminColors.gray100 ?? '#EBEBEB',
  },
  statusBadgeText: {
    fontFamily: FontFamily.medium,
    fontSize: 12,
  },
  statusActiveText: {
    color: AdminColors.green600,
  },
  statusInactiveText: {
    color: AdminColors.textMuted ?? '#737373',
  },
  confirmText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: AdminColors.textPrimary,
    marginBottom: 8,
  },
});

export default LoadSequenceConfig;
